import React, { useState, useEffect } from 'react';
import { Card, Table, Badge, Form, Button } from 'react-bootstrap';
import { Link, useNavigate } from 'react-router-dom';
import { getPagePopups, deletePagePopup } from '../../services/admin';
import PageTitle from '../../Component/Comman/PageTitle';

const PagePopups = () => {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);
    const [popups, setPopups] = useState([]);
    const [pagination, setPagination] = useState({
        page: 1,
        limit: 10,
        total: 0
    });
    const [filters, setFilters] = useState({
        status: '',
        search: ''
    });
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        fetchPopups();
    }, [pagination.page, pagination.limit, filters]);

    // Inject custom styles
    useEffect(() => {
        const pagePopupsStyles = `
            .btn-icon-xxs {
                width: 32px;
                height: 32px;
                padding: 0;
                display: inline-flex;
                align-items: center;
                justify-content: center;
                border-radius: 6px;
            }
            .fs-12 {
                font-size: 12px;
            }
        `;

        const styleId = 'page-popups-custom-styles';
        if (!document.getElementById(styleId)) {
            const style = document.createElement('style');
            style.id = styleId;
            style.innerHTML = pagePopupsStyles;
            document.head.appendChild(style);
        }
        return () => {
            const style = document.getElementById(styleId);
            if (style) {
                style.remove();
            }
        };
    }, []);

    const fetchPopups = async () => {
        try {
            setLoading(true);
            const params = {
                page: pagination.page,
                limit: pagination.limit,
                ...(filters.status && { status: filters.status }),
                ...(filters.search && { search: filters.search })
            };

            const response = await getPagePopups(params);
            if (response.success && response.data) {
                setPopups(response.data.popups || response.data || []);
                setPagination(prev => ({
                    ...prev,
                    total: response.data.pagination?.total || 0
                }));
            } else {
                setPopups([]);
            }
        } catch (error) {
            console.error('Error fetching popups:', error);
            alert(error.message || 'Failed to fetch popups');
            setPopups([]);
        } finally {
            setLoading(false);
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: value
        }));
        setPagination(prev => ({ ...prev, page: 1 }));
    };

    const handleDelete = async (id) => {
        const confirmed = window.confirm(
            "Are you sure you want to delete this popup? This action cannot be undone."
        );

        if (!confirmed) return;

        try {
            setProcessing(true);
            const response = await deletePagePopup(id);

            if (response.success) {
                alert("Popup deleted successfully");
                fetchPopups();
            } else {
                throw new Error(response.message || 'Failed to delete popup');
            }
        } catch (error) {
            console.error('Error deleting popup:', error);
            alert(error.message || 'Failed to delete popup');
        } finally {
            setProcessing(false);
        }
    };

    const getStatusBadge = (status) => {
        const badges = {
            active: 'success',
            inactive: 'danger'
        };
        return badges[status] || 'secondary';
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleDateString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric'
        });
    };

    const formatDateTime = (dateString) => {
        if (!dateString) return '-';
        const date = new Date(dateString);
        return date.toLocaleString('en-GB', {
            day: '2-digit',
            month: 'short',
            year: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    };

    return (
        <div className="container-xxl">
            <PageTitle pagetitle="Page Popups" sidebutton={false} />

            <div className="row">
                <div className="col-xl-12">
                    <Card className="border-0 shadow-sm">
                        <Card.Header className="bg-white border-bottom">
                            <div className="d-flex justify-content-between align-items-center">
                                <div>
                                    <h4 className="card-title mb-0">Page Popups</h4>
                                    <small className="text-muted">Manage page popups and notifications</small>
                                </div>
                                <Button 
                                    variant="primary" 
                                    className="btn-rounded"
                                    onClick={() => navigate('/admin/create-popup')}
                                >
                                    <i className="fa fa-plus me-2"></i>Create Popup
                                </Button>
                            </div>
                        </Card.Header>
                        <Card.Body>
                            <div className="mb-4">
                                <div className="row">
                                    <div className="col-md-3">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Status</Form.Label>
                                            <Form.Select
                                                name="status"
                                                value={filters.status}
                                                onChange={handleFilterChange}
                                            >
                                                <option value="">All</option>
                                                <option value="active">Active</option>
                                                <option value="inactive">Inactive</option>
                                            </Form.Select>
                                        </Form.Group>
                                    </div>
                                    <div className="col-md-3">
                                        <Form.Group className="mb-3">
                                            <Form.Label>Search</Form.Label>
                                            <Form.Control
                                                type="text"
                                                name="search"
                                                value={filters.search}
                                                onChange={handleFilterChange}
                                                placeholder="Search popups..."
                                            />
                                        </Form.Group>
                                    </div>
                                </div>
                            </div>

                            <div className="table-responsive">
                                <Table className="table table-responsive-md table-hover">
                                    <thead className="table-light">
                                        <tr>
                                            <th><strong>Title</strong></th>
                                            <th><strong>Status</strong></th>
                                            <th><strong>Display Period</strong></th>
                                            <th><strong>Created At</strong></th>
                                            <th><strong>Actions</strong></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {loading ? (
                                            <tr>
                                                <td colSpan="5" className="text-center">
                                                    <div className="py-5">
                                                        <div className="spinner-border text-primary" role="status">
                                                            <span className="visually-hidden">Loading...</span>
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : !Array.isArray(popups) || popups.length === 0 ? (
                                            <tr>
                                                <td colSpan="5" className="text-center">
                                                    <div className="d-flex justify-content-center align-items-center flex-column py-5">
                                                        <i className="fa fa-window-maximize fa-3x text-muted mb-3"></i>
                                                        <h4 className="text-muted">No popups found</h4>
                                                    </div>
                                                </td>
                                            </tr>
                                        ) : (
                                            popups.map((popup) => (
                                                <tr key={popup._id}>
                                                    <td>
                                                        <div className="d-flex align-items-center">
                                                            {popup.image && (
                                                                <img 
                                                                    src={(() => {
                                                                        const baseUrl = process.env.REACT_APP_IMAGE_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
                                                                        let imgPath = popup.image.replace(/\\/g, '/');
                                                                        if (!imgPath.startsWith('/')) {
                                                                            imgPath = `/${imgPath}`;
                                                                        }
                                                                        const cleanBaseUrl = baseUrl.endsWith('/') ? baseUrl.slice(0, -1) : baseUrl;
                                                                        return `${cleanBaseUrl}${imgPath}`;
                                                                    })()}
                                                                    alt={popup.title}
                                                                    className="rounded me-2"
                                                                    style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                                                                    onError={(e) => {
                                                                        e.target.style.display = 'none';
                                                                    }}
                                                                />
                                                            )}
                                                            <div>
                                                                <strong>{popup.title}</strong>
                                                                <div className="text-muted fs-12">
                                                                    Target: {popup.targetUsers === 'all' ? 'All Users' : 'Specific Users'}
                                                                </div>
                                                            </div>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <Badge bg={getStatusBadge(popup.status)}>
                                                            {popup.status.charAt(0).toUpperCase() + popup.status.slice(1)}
                                                        </Badge>
                                                    </td>
                                                    <td>
                                                        <div>
                                                            <div>From: {formatDate(popup.displayFrom)}</div>
                                                            {popup.displayUntil && (
                                                                <div className="text-muted fs-12">
                                                                    Until: {formatDate(popup.displayUntil)}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td>{formatDateTime(popup.createdAt)}</td>
                                                    <td>
                                                        <div className="d-flex gap-1">
                                                            <Button
                                                                variant="primary"
                                                                size="sm"
                                                                className="btn-icon-xxs"
                                                                onClick={() => navigate(`/admin/edit-popup/${popup._id}`)}
                                                                title="Edit"
                                                            >
                                                                <i className="fa fa-edit"></i>
                                                            </Button>
                                                            <Button
                                                                variant="danger"
                                                                size="sm"
                                                                className="btn-icon-xxs"
                                                                onClick={() => handleDelete(popup._id)}
                                                                disabled={processing}
                                                                title="Delete"
                                                            >
                                                                <i className="fa fa-trash"></i>
                                                            </Button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            {!loading && Array.isArray(popups) && popups.length > 0 && (
                                <div className="d-flex justify-content-between align-items-center mt-3">
                                    <div>
                                        Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} entries
                                    </div>
                                    <div>
                                        <Button 
                                            variant="primary" 
                                            size="sm" 
                                            className="me-2" 
                                            disabled={pagination.page === 1}
                                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                                        >
                                            Previous
                                        </Button>
                                        <Button 
                                            variant="primary" 
                                            size="sm" 
                                            disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
                                            onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                                        >
                                            Next
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </Card.Body>
                    </Card>
                </div>
            </div>
        </div>
    );
};

export default PagePopups;
